import React, { useState, useEffect } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { Eye, EyeOff, LogIn, Wrench } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useForm } from '../hooks/useForm'
import { useToast } from '../components/Toast'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth()
  const { error: showError } = useToast()
  const [showPassword, setShowPassword] = useState(false)

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    getFieldProps
  } = useForm({
    initialValues: {
      email: '',
      password: ''
    },
    validationRules: {
      email: {
        required: 'Email é obrigatório',
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: 'Email inválido'
        }
      },
      password: {
        required: 'Senha é obrigatória',
        minLength: { value: 6, message: 'Senha deve ter pelo menos 6 caracteres' }
      }
    },
    onSubmit: async (formValues) => {
      try {
        await login(formValues)
      } catch (err: any) {
        showError('Erro no login', err.message)
      }
    }
  })

  // Redirecionar se já estiver logado
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center bg-primary-600 rounded-full">
            <Wrench className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            NexaMakers OS
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sistema de Gestão de Assistências Técnicas
          </p>
        </div>

        {/* Formulário */}
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="seu@email.com"
              required
              {...getFieldProps('email')}
            />

            <div className="relative">
              <Input
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Sua senha"
                required
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
                {...getFieldProps('password')}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={isSubmitting}
              leftIcon={<LogIn className="h-4 w-4" />}
            >
              Entrar
            </Button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Não tem uma conta?{' '}
              <Link
                to="/register"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Criar conta
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>© 2025 NexaMakers. Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage